<script setup>
import { useForm } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ChecklistHistoryDrawer from '@/components/admin/report-qc/ChecklistHistoryDrawer.vue'
import ChecklistPreviewPanel from '@/components/admin/report-qc/ChecklistPreviewPanel.vue'
import ChecklistTrial from '@/components/admin/report-qc/ChecklistTrial.vue'
import { useChecklistPreview } from '@/composables/useChecklistPreview.js'
import { useUnsavedGuard } from '@/composables/useUnsavedGuard.js'

/**
 * RED-385: Üldjuhis, the text that goes to the engine with every question
 * (design-spec §3). Three sections are editable; the checklist slot and
 * VÄLJUNDI VORM are shown grey and are never part of the payload. The parts
 * are shown in the order the engine receives them. Every save is a new
 * checklist version with a note; the server checks every rule.
 */
const props = defineProps({
  checklist: { type: Object, required: true },
  versions: { type: Array, default: undefined },
  trial: { type: Object, required: true },
})

const { t } = useI18n()
const toast = useToast()

const SECTIONS = ['intro', 'heading', 'tone']

const frameOf = source => Object.fromEntries(SECTIONS.map(section => [section, source?.[section] ?? '']))

const form = useForm('put', route('admin.report-qc.checklist.update'), { frame: frameOf(props.checklist.frame), note: '' })
form.setValidationTimeout(400)

const historyOpen = ref(false)
const showPreview = ref(false)

// RED-386: "Mis läheb mootorile" with the unsaved sections (only while the panel is open).
const { preview, loading: previewLoading, failed: previewFailed } = useChecklistPreview(() => (showPreview.value ? { frame: form.frame } : null))

const frameChanged = computed(() => SECTIONS.some(section => form.frame[section] !== props.checklist.frame[section]))

useUnsavedGuard(() => frameChanged.value || form.note.trim() !== '')

// A save or a restore brings new props: the saved text becomes the new baseline.
watch(
  () => props.checklist.frame,
  frame => {
    form.defaults({ frame: frameOf(frame), note: '' })
    form.reset()
    form.clearErrors()
  },
  { deep: true },
)

SECTIONS.forEach(section => watch(() => form.frame[section], () => form.validate(`frame.${section}`)))

const sectionError = section => form.errors[`frame.${section}`]

const charCount = computed(() =>
  new Intl.NumberFormat('et-EE').format(
    props.checklist.fixedChars + SECTIONS.reduce((sum, section) => sum + form.frame[section].length, 0),
  ),
)

const save = () => {
  form.put(route('admin.report-qc.checklist.update'), {
    preserveScroll: true,
    onSuccess: page => {
      toast.add({
        severity: 'success',
        summary: t('messages.admin.report_qc.settings.saved'),
        detail: t('messages.admin.report_qc.uldjuhis.saved_detail', { n: page.props.checklist.versionNumber }),
        life: 4000,
      })
    },
  })
}
</script>

<template>
  <div class="flex flex-col gap-6" data-testid="general-instructions">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-xl font-medium">{{ t('messages.admin.report_qc.nav.uldjuhis') }}</h2>
        <p v-if="checklist.versionNumber" class="text-sm text-gray-600" data-testid="uldjuhis-version">
          {{ t('messages.admin.report_qc.uldjuhis.version', { n: checklist.versionNumber }) }}
          <i v-if="checklist.hash" v-tooltip.top="t('messages.admin.report_qc.uldjuhis.version_hash_tooltip', { hash: checklist.hash })" class="pi pi-info-circle ml-1 text-xs text-gray-400" :aria-label="t('messages.admin.report_qc.uldjuhis.version_hash_tooltip', { hash: checklist.hash })" data-testid="uldjuhis-version-hash" />
        </p>
      </div>
      <div class="flex flex-wrap items-center gap-4">
        <div class="flex items-center gap-2">
          <ToggleSwitch v-model="showPreview" input-id="rqc-uldjuhis-preview" data-testid="uldjuhis-preview-toggle" />
          <label for="rqc-uldjuhis-preview" class="text-sm">{{ t('messages.admin.report_qc.preview.toggle') }}</label>
        </div>
        <Button
          :label="t('messages.admin.report_qc.history.button')"
          icon="pi pi-history"
          severity="secondary"
          outlined
          data-testid="uldjuhis-history-open"
          @click="historyOpen = true"
        />
      </div>
    </div>

    <div class="flex flex-col gap-6 xl:flex-row xl:items-start">
      <div class="flex min-w-0 flex-1 flex-col gap-6">

        <Message v-if="!checklist.versionNumber" severity="info" :closable="false">
          {{ t('messages.admin.report_qc.uldjuhis.no_version_yet') }}
        </Message>

        <Card>
          <template #content>
            <Fluid>
              <p class="mb-4 text-sm text-gray-600">{{ t('messages.admin.report_qc.uldjuhis.intro') }}</p>

              <div class="flex flex-col gap-5">
                <div>
                  <label for="rqc-uldjuhis-intro" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.uldjuhis.label_intro') }}</label>
                  <p class="mb-1 text-xs text-gray-500" data-testid="uldjuhis-intro-hint">{{ t('messages.admin.report_qc.uldjuhis.hint_intro') }}</p>
                  <Textarea
                    id="rqc-uldjuhis-intro"
                    v-model="form.frame.intro"
                    rows="12"
                    auto-resize
                    class="font-mono text-sm"
                    :maxlength="checklist.sectionMax.intro"
                    :invalid="!!sectionError('intro')"
                    data-testid="uldjuhis-intro"
                  />
                  <p class="mt-1 text-sm text-red-600" :class="{ invisible: !sectionError('intro') }" data-testid="uldjuhis-intro-error">{{ sectionError('intro') || '&nbsp;' }}</p>
                </div>

                <div data-testid="uldjuhis-output-format">
                  <div class="mb-1 flex items-center gap-2">
                    <i class="pi pi-lock text-gray-400" aria-hidden="true" />
                    <span class="text-sm font-medium text-gray-600">{{ t('messages.admin.report_qc.uldjuhis.locked') }}</span>
                  </div>
                  <pre class="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm whitespace-pre-wrap text-gray-500">{{ checklist.outputFormat }}</pre>
                  <p class="mt-1 text-xs text-gray-500">{{ t('messages.admin.report_qc.uldjuhis.output_format_hint') }}</p>
                </div>

                <div>
                  <label for="rqc-uldjuhis-heading" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.uldjuhis.label_heading') }}</label>
                  <p class="mb-1 text-xs text-gray-500" data-testid="uldjuhis-heading-hint">{{ t('messages.admin.report_qc.uldjuhis.hint_heading') }}</p>
                  <InputText
                    id="rqc-uldjuhis-heading"
                    v-model="form.frame.heading"
                    class="font-mono text-sm"
                    :maxlength="checklist.sectionMax.heading"
                    :invalid="!!sectionError('heading')"
                    data-testid="uldjuhis-heading"
                  />
                  <p class="mt-1 text-sm text-red-600" :class="{ invisible: !sectionError('heading') }" data-testid="uldjuhis-heading-error">{{ sectionError('heading') || '&nbsp;' }}</p>
                </div>

                <div
                  class="flex items-center gap-2 rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-500"
                  data-testid="uldjuhis-slot"
                >
                  <i class="pi pi-lock text-gray-400" aria-hidden="true" />
                  <span>{{ t('messages.admin.report_qc.uldjuhis.slot', { n: checklist.itemCount }) }}</span>
                </div>

                <div>
                  <label for="rqc-uldjuhis-tone" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.uldjuhis.label_tone') }}</label>
                  <p class="mb-1 text-xs text-gray-500" data-testid="uldjuhis-tone-hint">{{ t('messages.admin.report_qc.uldjuhis.hint_tone') }}</p>
                  <Textarea
                    id="rqc-uldjuhis-tone"
                    v-model="form.frame.tone"
                    rows="4"
                    auto-resize
                    class="font-mono text-sm"
                    :maxlength="checklist.sectionMax.tone"
                    :invalid="!!sectionError('tone')"
                    data-testid="uldjuhis-tone"
                  />
                  <p class="mt-1 text-sm text-red-600" :class="{ invisible: !sectionError('tone') }" data-testid="uldjuhis-tone-error">{{ sectionError('tone') || '&nbsp;' }}</p>
                </div>
              </div>

              <p class="text-right text-xs text-gray-500" data-testid="uldjuhis-char-count">
                {{ t('messages.admin.report_qc.uldjuhis.char_count', { n: charCount }) }}
              </p>
            </Fluid>
          </template>
        </Card>

        <Card>
          <template #content>
            <Fluid>
              <label for="rqc-uldjuhis-note" class="mb-1 block text-sm font-medium">{{ t('messages.admin.report_qc.settings.note') }}</label>
              <Textarea
                id="rqc-uldjuhis-note"
                v-model="form.note"
                rows="2"
                auto-resize
                :invalid="!!form.errors.note"
                data-testid="uldjuhis-note"
              />
              <p class="mt-1 text-sm text-red-600" :class="{ invisible: !form.errors.note }" data-testid="uldjuhis-note-error">{{ form.errors.note || '&nbsp;' }}</p>
              <div class="mt-2 flex flex-wrap items-end justify-end gap-2">
                <ChecklistTrial
                  :draft="() => ({ frame: form.frame })"
                  :draft-hash="frameChanged ? (preview?.hash ?? null) : trial.currentHash"
                  :disabled="form.processing"
                  :trial="trial"
                />
                <Button
                  :label="t('messages.admin.report_qc.settings.save')"
                  icon="pi pi-check"
                  class="w-auto!"
                  :loading="form.processing"
                  :disabled="!frameChanged || form.processing"
                  data-testid="uldjuhis-save"
                  @click="save"
                />
              </div>
            </Fluid>
          </template>
        </Card>
      </div>

      <aside v-if="showPreview" class="w-full xl:sticky xl:top-4 xl:w-[28rem] xl:shrink-0">
        <ChecklistPreviewPanel :preview="preview" :loading="previewLoading" :failed="previewFailed" />
      </aside>
    </div>

    <ChecklistHistoryDrawer v-model:visible="historyOpen" :versions="versions" />
  </div>
</template>
